import { useEffect, useState } from "react";
import { Send, MessageSquarePlus, Flag } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { LOG_META } from "../../pages/MainCourante";
import { SearchSelect } from "../SearchSelect";

const when = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Entry({ e, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [label, Icon, cls] = LOG_META[e.kind] || LOG_META.note;
  const add = async (ev) => {
    ev.preventDefault(); setErr("");
    try { await http.post(`/agent-app/logbook/${e.id}/complement`, { text }); setText(""); setOpen(false); onChanged(); } catch (er) { setErr(formatApiError(er)); }
  };
  const closeRun = async () => {
    if (!window.confirm("Clôturer la ronde de votre collègue ? Les points non validés seront notés comme manqués.")) return;
    try { await http.post(`/agent-app/rounds/run/${e.ref_id}/finish`); onChanged(); } catch (er) { setErr(formatApiError(er)); }
  };
  return (
    <div className="px-4 py-3 flex items-start gap-3" data-testid={`agent-log-${e.id}`}>
      <span className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${cls}`}><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm"><span className="font-semibold">{label}</span> — {e.text}</div>
        <div className="text-xs text-slate-500">{when(e.at)} · {e.site_name}{!e.mine && <span className="ml-1 font-semibold text-sky-700">· {e.agent_name}</span>}</div>
        {(e.additions || []).map((a, i) => <div key={i} className="mt-1 text-xs bg-slate-50 border-l-2 border-sky-300 pl-2 py-0.5"><b>{a.agent_name}</b> ({when(a.at)}) : {a.text}</div>)}
        <div className="flex gap-3 mt-1">
          <button data-testid={`agent-log-complement-${e.id}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700"><MessageSquarePlus className="h-3.5 w-3.5" />Compléter</button>
          {e.closable_run && <button data-testid={`agent-log-close-run-${e.id}`} onClick={closeRun} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700"><Flag className="h-3.5 w-3.5" />Clôturer la ronde</button>}
        </div>
        {open && (
          <form onSubmit={add} className="mt-2 flex gap-1">
            <input data-testid={`agent-log-complement-text-${e.id}`} required value={text} onChange={(x) => setText(x.target.value)} placeholder="Complément d'information" className="flex-1 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
            <button data-testid={`agent-log-complement-send-${e.id}`} className="bg-sky-600 text-white rounded-lg px-3"><Send className="h-4 w-4" /></button>
          </form>
        )}
        {err && <div className="text-xs text-rose-700 mt-1">{err}</div>}
      </div>
    </div>
  );
}

export function AgentLogbook() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ site_id: "", text: "" });
  const [err, setErr] = useState("");
  const load = () => http.get("/agent-app/logbook").then((r) => setItems(r.data));
  useEffect(() => { load(); http.get("/agent-app/report-sites").then((r) => { setSites(r.data); if (r.data[0]) setF((x) => ({ ...x, site_id: r.data[0].id })); }); }, []);
  const add = async (e) => {
    e.preventDefault(); setErr("");
    try { await http.post("/agent-app/logbook", f); setF({ ...f, text: "" }); load(); } catch (er) { setErr(formatApiError(er)); }
  };
  return (
    <div className="space-y-4" data-testid="agent-logbook">
      {sites.length > 0 && (
        <form onSubmit={add} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="agent-logbook-form">
          <h2 className="font-display text-lg font-extrabold">Ajouter à la main courante</h2>
          <SearchSelect data-testid="logbook-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white">
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SearchSelect>
          <textarea data-testid="logbook-text" required rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Ex. 22h10 : ronde extérieure RAS, portail fermé" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
          {err && <div className="text-sm text-rose-700" data-testid="logbook-error">{err}</div>}
          <button data-testid="logbook-submit" className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-lg py-2.5"><Send className="h-4 w-4" />Enregistrer</button>
        </form>
      )}
      <section>
        <h2 className="font-display text-lg font-extrabold mb-1">Activité de ma vacation</h2>
        <p className="text-xs text-slate-500 mb-2">Vos événements et ceux de vos collègues sur la même vacation. Vous pouvez les compléter, sans modifier ce qui est déjà enregistré.</p>
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="agent-logbook-list">
          {items.length === 0 && <div className="p-5 text-center text-sm text-slate-500">Aucune activité enregistrée.</div>}
          {items.map((e) => <Entry key={e.id} e={e} onChanged={load} />)}
        </div>
      </section>
    </div>
  );
}
